import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { createAdminUser, updateAdminUser, resetAdminUserPassword, deleteAdminUser, getMasterDiagnosticDepartments } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

// Doctor logins are provisioned from Doctor Master instead (1:1 with a
// clinical roster record) — this form only manages staff accounts.
const ROLE_OPTIONS = [
  { value: "super-admin", label: "Super Admin" },
  { value: "admin", label: "Admin — everything except Master (Doctors included) & User Master" },
  { value: "caredesk", label: "Care Desk" },
  { value: "finance", label: "Finance" },
  { value: "master", label: "Master" },
  { value: "technician", label: "Technician — only their diagnostic department's worklist" },
];

const EMPTY = { name: "", email: "", phone: "", password: "", roles: [], department_ids: [] };

export default function UserModal({ open, onOpenChange, user, currentUserId, onSaved, onPasswordReset }) {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = !!user;
  const isSelf = isEdit && user._id === currentUserId;
  const isSuperAdmin = isEdit && user.roles.includes("super-admin");
  const [departments, setDepartments] = useState([]);
  const isTechnician = form.roles.includes("technician");

  useEffect(() => {
    if (!open) return;
    getMasterDiagnosticDepartments()
      .then((res) => setDepartments(res.data.filter((d) => d.active || user?.department_ids?.includes(d._id))))
      .catch(() => setDepartments([]));
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    setConfirmingDelete(false);
    setShowPassword(false);
    if (user) {
      setForm({
        name: user.name,
        email: user.email,
        phone: user.phone || "",
        password: "",
        roles: user.roles,
        department_ids: (user.department_ids || []).map(String),
      });
      setStatus(user.status);
    } else {
      setForm(EMPTY);
      setStatus("active");
    }
  }, [open, user]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdminUser(user._id);
      toast.success("User deleted.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Could not delete user.");
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    setResettingPassword(true);
    try {
      const res = await resetAdminUserPassword(user._id);
      onOpenChange(false);
      onPasswordReset?.({ name: form.name, email: res.data.email, password: res.data.password });
    } catch (err) {
      toast.error(err?.message || "Could not reset password.");
    } finally {
      setResettingPassword(false);
    }
  };

  const toggleRole = (role) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || (!isEdit && !form.email)) {
      toast.error("Name and email or phone are required.");
      return;
    }
    if (!isEdit && (!form.password || form.password.length < 8)) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (form.roles.length === 0) {
      toast.error("Select at least one role.");
      return;
    }
    if (isTechnician && form.department_ids.length === 0) {
      toast.error("Pick at least one department for a technician.");
      return;
    }
    const department_ids = isTechnician ? form.department_ids : [];

    setSaving(true);
    try {
      if (isEdit) {
        await updateAdminUser(user._id, {
          name: form.name,
          phone: form.phone || null,
          roles: form.roles,
          department_ids,
          status,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("User updated.");
      } else {
        await createAdminUser({
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          password: form.password,
          roles: form.roles,
          ...(isTechnician ? { department_ids } : {}),
        });
        toast.success("User created.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Could not save user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit user" : "Add user"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Email or phone *</Label>
            <Input
              type="text"
              value={form.email}
              disabled={isEdit}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (for click-to-call)</Label>
            <Input
              type="text"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="9101234567"
            />
            <p className="text-xs text-muted-foreground">
              Only needed if "Email or phone" above isn't already a phone number — that's what click-to-call
              rings first before connecting to the patient.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>{isEdit ? "New password (leave blank to keep current)" : "Password *"}</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {isEdit && (
              <div>
                <Button type="button" size="sm" variant="link" className="h-auto p-0" disabled={resettingPassword} onClick={handleResetPassword}>
                  {resettingPassword ? "Generating…" : "Generate a new password for them"}
                </Button>
                <p className="text-xs text-muted-foreground">Since passwords are encrypted, this is the only way to view/hand over a working password again.</p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Roles *</Label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={form.roles.includes(r.value)}
                    onCheckedChange={() => toggleRole(r.value)}
                    disabled={isSelf && r.value === "super-admin"}
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>

          {isTechnician && (
            <div className="space-y-1.5">
              <Label>Departments *</Label>
              {departments.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No diagnostic departments yet — add them under Master → Diagnostic Departments first.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {departments.map((d) => (
                    <label key={d._id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={form.department_ids.includes(d._id)}
                        onCheckedChange={() =>
                          setForm((f) => ({
                            ...f,
                            department_ids: f.department_ids.includes(d._id)
                              ? f.department_ids.filter((id) => id !== d._id)
                              : [...f.department_ids, d._id],
                          }))
                        }
                      />
                      {d.name}
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">They'll only see and upload reports for these departments' tests.</p>
            </div>
          )}

          {isEdit && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={isSelf}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {isSelf && <p className="text-xs text-muted-foreground">You can't deactivate your own account or remove your own Super Admin role.</p>}
            </div>
          )}

          <DialogFooter>
            {isEdit && (
              <Button
                type="button"
                variant="destructive"
                className="sm:mr-auto"
                disabled={isSelf}
                title={isSelf ? "You can't delete your own account" : undefined}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete user
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <Dialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {form.name || "this user"}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {isSuperAdmin
              ? `This account is a Super Admin. Deleting it removes their ability to log in and manage the system — make sure another active Super Admin exists before continuing. This can't be undone.`
              : `This removes their dashboard access immediately. This can't be undone.`}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" disabled={deleting} onClick={handleDelete}>
              {deleting ? "Deleting…" : isSuperAdmin ? "Yes, delete this Super Admin" : "Yes, delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
